import { z } from 'zod';
import { LessonStatus, LessonType } from './enums';
import { uuid } from './fields';

/**
 * Lessons — takeaways captured on a Trip, reviewable back into a Master List
 * (data-model.md §12, PRD §15). Review-and-approve — NEVER auto-applies. REST
 * endpoints land in T33/T35; DTO shapes pinned now.
 *
 * Code-enforced invariants (data-model.md §12 — API logic, not schema):
 * capture only sets `status='open'`; `note` carries no template action and
 * resolves `targetMasterListId=NULL`; `forgot`/`didnt_need` resolve the target
 * from the linked item's origin; viewer lessons cannot be applied.
 */

/** Full Lesson resource (response shape). */
export const Lesson = z.object({
  id: uuid(),
  tripId: uuid(),
  tripItemId: uuid().nullable(),
  type: LessonType,
  text: z.string().trim().min(1),
  status: LessonStatus,
  targetMasterListId: uuid().nullable(),
  createdBy: uuid(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Lesson = z.infer<typeof Lesson>;

/**
 * Capture body (T33). Server sets `tripId` from the path, `createdBy` = current
 * user, `status='open'`, and resolves `targetMasterListId` from the linked
 * item's origin when the type is `forgot`/`didnt_need`. `text` required for all
 * types. `tripItemId` optional link to a specific item.
 */
export const CreateLessonInput = z.object({
  type: LessonType,
  text: z.string().trim().min(1).max(2000),
  tripItemId: uuid().nullable().optional(),
});
export type CreateLessonInput = z.infer<typeof CreateLessonInput>;

/**
 * Review action (T35): edit-then-apply or dismiss. Status moves open → applied
 * / dismissed; `text` may be tweaked before applying. Apply rights (owner/editor
 * of the target list) are enforced in the handler.
 */
export const UpdateLessonInput = z
  .object({
    text: z.string().trim().min(1).max(2000),
    status: LessonStatus,
  })
  .partial();
export type UpdateLessonInput = z.infer<typeof UpdateLessonInput>;
